import React from 'react';
import { Handle, Position } from 'reactflow';
import { MessageSquare, User, CheckCircle, XCircle, Clock, Trash2, AlertCircle, Sparkles, Send, Play } from 'lucide-react';
import { NodeData } from '../../../types';
import { useFlowStore } from '../../../store';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Tooltip from '../../common/Tooltip';
import { NodeLabel } from './shared';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';

interface UserPromptNodeProps {
  data: NodeData;
  selected: boolean;
  onOpen?: (nodeId: string) => void;
  onSendMessage?: (message: string) => Promise<void>;
}

const UserPromptNode: React.FC<UserPromptNodeProps> = ({ data, selected, onOpen, onSendMessage }) => {
  const { nodes, edges, setNodes, setEdges } = useFlowStore();
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // AC-1406: Delete via React Query mutation hook.
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');

  // Check if node can be deleted (not referenced by other nodes)
  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      n =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id)
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id]);

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;

    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });

      // Remove the node and its connected edges from local canvas state
      // for an immediate UX response. The mutation hook's invalidation
      // refreshes the steps cache; the seed effect will rebuild from it.
      setNodes(nodes.filter(node => node.id !== data.id));
      setEdges(edges.filter(edge => edge.source !== data.id && edge.target !== data.id));
    } catch (error) {
      console.error('Failed to delete step:', error);
      // Show error message to user
    }
  };

  const handleSubmitInput = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || !onSendMessage || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSendMessage(inputValue.trim());
      setInputValue('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'waiting_for_input':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  // Check if this is the first step (initiator)
  const isFirstStep = !data.step?.previous_step_id;

  const getStatusColor = () => {
    switch (data.status) {
      case 'waiting_for_input':
        return 'bg-yellow-50 dark:bg-yellow-900/80';
      case 'completed':
        return 'bg-green-50 dark:bg-green-900/80';
      case 'failed':
        return 'bg-red-50 dark:bg-red-900/80';
      default:
        return 'bg-white dark:bg-cap-dark';
    }
  };

  const getBorderColor = () => {
    if (isFirstStep) {
      return 'border-green-500';
    }
    switch (data.status) {
      case 'waiting_for_input':
        return 'border-yellow-500';
      case 'completed':
        return 'border-green-500';
      case 'failed':
        return 'border-red-500';
      default:
        return 'border-gray-300 dark:border-gray-600';
    }
  };

  return (
    <div className="relative">
      <div
        className={`relative w-72 h-32 px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          ${getBorderColor()}
          bg-gradient-to-br ${getStatusColor()}
          hover:shadow-xl hover:border-opacity-100`}
      >
        {/* Start badge - show when this is the first step, clickable to start execution.
            Positioned outside the card (top-left corner) so it doesn't overlap content. */}
        {isFirstStep && (
          <button
            title="Click to start orchestration"
            onClick={(e) => {
              e.stopPropagation();
              data.onStartExecution?.();
            }}
            className="absolute -top-3 -left-2 px-2 py-1 rounded-full bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold shadow-md whitespace-nowrap z-20 flex items-center gap-1 cursor-pointer hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 transition-all duration-150"
          >
            <Play className="w-3 h-3" />
            <span>Start</span>
          </button>
        )}

        {/* Delete button - only show if node is deletable */}
        {isDeletable && (
          <button
              title="Delete step"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
        )}

        {/* Target handles for dynamic edge connections */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />

        {/* User Prompt Badge - Similar to AgentNode */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            <span>User Prompt</span>
            {data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0 && (
              <Tooltip content="Feedback Loop Hints configured">
                <div>
                  <Sparkles className="w-3 h-3" />
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Main Card Content */}
        <div className={`flex flex-col items-center pb-1 w-full overflow-hidden ${data.label && !['user_prompt', 'User Prompt', 'User Input'].includes(data.label) ? 'pt-3' : 'pt-5'}`}>
          {/* Only show label row if user has set a custom one (not the default type name) */}
          {data.label && !['user_prompt', 'User Prompt', 'User Input'].includes(data.label) ? (
            <div className="flex items-center gap-2 mb-1 w-full justify-center">
              <NodeLabel
                label={data.label}
                maxLength={20}
                fontWeight="medium"
                textColor="text-gray-900 dark:text-warm-white"
              />
              {data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0 && (
                <Tooltip content="Feedback Loop Hints configured">
                  <div>
                    <Sparkles className="w-4 h-4 text-purple-500" />
                  </div>
                </Tooltip>
              )}
              {getStatusIcon()}
            </div>
          ) : (
            /* When no custom label, show status/hints only if they exist */
            (getStatusIcon() || (data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0)) && (
              <div className="flex items-center gap-2 mb-1 w-full justify-center">
                {data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0 && (
                  <Tooltip content="Feedback Loop Hints configured">
                  <div>
                    <Sparkles className="w-4 h-4 text-purple-500" />
                  </div>
                  </Tooltip>
                )}
                {getStatusIcon()}
              </div>
            )
          )}
          
          {/* Input Message Display */}
          {data.step?.input_message && (
            <div className={`px-2 py-1 rounded bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 text-xs border border-orange-200 dark:border-orange-800 mx-2 self-stretch overflow-hidden ${isFirstStep ? 'mt-1' : ''}`}>
              <div className="text-xs leading-snug break-words line-clamp-2">
                "{data.step.input_message}"
              </div>
            </div>
          )}
        </div>

        {/* Input field for waiting_for_input status */}
        {data.status === 'waiting_for_input' && onSendMessage && (
          <form onSubmit={handleSubmitInput} className="mt-3 space-y-2">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your response..."
                disabled={isSubmitting}
                className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                autoFocus
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isSubmitting}
                className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-1"
              >
                {isSubmitting ? (
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-3 h-3" />
                )}
              </button>
            </div>
          </form>
        )}

        {data.status === 'waiting_for_input' && (
          <div className="absolute inset-0 rounded-lg border-2 border-yellow-500 animate-pulse" />
        )}

        {/* Source handles for dynamic outgoing connections */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        
        {/* Dedicated feedback loop handles - positioned to avoid conflicts */}
        <Handle
          type="source"
          position={Position.Top}
          id="feedback-out-top"
          style={{ top: 0, left: '30%', transform: 'translate(-50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="feedback-out-left"
          style={{ left: 0, top: '30%', transform: 'translate(-50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Top}
          id="feedback-in-top"
          style={{ top: 0, left: '70%', transform: 'translate(-50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="feedback-in-right"
          style={{ right: 0, top: '30%', transform: 'translate(50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />

        {/* Additional feedback handles for assistant loops */}
        <Handle
          type="source"
          position={Position.Right}
          id="feedback-out-right"
          style={{ right: 0, top: '70%', transform: 'translate(50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="feedback-out-bottom"
          style={{ left: '30%', bottom: 0, transform: 'translate(-50%, 50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="feedback-in-left"
          style={{ left: 0, top: '70%', transform: 'translate(-50%, -50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="feedback-in-bottom"
          style={{ left: '70%', bottom: 0, transform: 'translate(-50%, 50%)' }}
          className="w-2 h-2 !bg-purple-500 !border-2 !border-white rounded-sm"
        />
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Step"
          message="Are you sure you want to delete this step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default UserPromptNode;